import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Factory, Lock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function fmtErr(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters"); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); setDone(true); }
    catch (e2) { setErr(fmtErr(e2?.response?.data?.detail) || "Could not reset password"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center"><Factory className="w-6 h-6 text-primary-foreground" /></div>
          <div className="font-display font-bold text-2xl tracking-wide">TEXFLOW</div>
        </div>
        {done ? (
          <div className="text-center" data-testid="reset-done">
            <div className="w-14 h-14 rounded-2xl bg-success/15 text-success flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7" /></div>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Password updated</h2>
            <p className="text-sm text-muted-foreground mt-2">You can now sign in with your new password.</p>
            <Button className="mt-6 w-full" onClick={() => navigate("/login")} data-testid="reset-go-login">Go to sign in</Button>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-invalid">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Invalid link</h2>
            <p className="text-sm text-muted-foreground mt-2">This password reset link is missing or invalid. Please request a new one from the sign-in page.</p>
            <Button className="mt-6 w-full" variant="outline" onClick={() => navigate("/login")}>Back to sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Set a new password</h2>
            <p className="text-sm text-muted-foreground mt-2 mb-5">Choose a new password for your account.</p>
            <Label htmlFor="pw" className="text-xs">New password</Label>
            <div className="relative mt-1"><Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Min 6 characters" className="pl-9" data-testid="reset-password-input" /></div>
            {err && <p className="text-sm text-destructive mt-2" data-testid="reset-error">{err}</p>}
            <Button type="submit" disabled={busy} className="mt-5 w-full" data-testid="reset-submit">{busy ? "Saving…" : "Update password"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
